import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { useUpdateClinician } from '@/hooks/queries';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { cn } from '@/lib/utils';
import { LANGUAGES, resolveLanguageCode, type LanguageCode } from '@/i18n/languages';

type LangCode = LanguageCode;

/**
 * Small dropdown in the header to switch i18next between en / fr.
 *
 * Persistence happens in two places:
 * 1. The `i18n` init module listens for `languageChanged` and writes to
 *    `localStorage["i18nextLng"]` + the `i18next` cookie. This makes the
 *    choice survive reloads on the same device immediately.
 * 2. We also `PATCH /hospitals/{h}/clinicians/{c}` with `{locale}` so the
 *    profile is the authoritative source — matches v1 behavior and lets
 *    the choice follow the user to a new device. Fire-and-forget; if the
 *    PATCH fails the user still has the local cookie/storage to fall back
 *    on, and `useUserDefaultLanguage` won't overwrite it because the
 *    profile fetch will return the *previous* locale (which is what we
 *    just changed away from on the client).
 */
export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const current = resolveLanguageCode(i18n.language);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const clinician = useAuthStore((s) => s.clinician);
  const setClinician = useAuthStore((s) => s.setClinician);
  const updateClinician = useUpdateClinician();

  // Click outside → close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (code: LangCode) => {
    setOpen(false);
    if (code === current) return;
    i18n.changeLanguage(code);
    // Optimistically update the auth-store clinician so a profile re-fetch
    // doesn't temporarily flip the language back via useUserDefaultLanguage
    // before the PATCH completes.
    if (clinician?.id) {
      const hospitalId = clinicianHomeHospitalId(clinician);
      setClinician({ ...clinician, locale: code });
      if (hospitalId != null) {
        updateClinician.mutate({
          hospitalId,
          clinicianId: clinician.id,
          body: { locale: code },
        });
      }
    }
  };

  return (
    <div ref={wrapper} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Languages className="mr-2 h-4 w-4" />
        <span className="uppercase">{current}</span>
      </Button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-40 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === current;
            return (
              <li key={lang.code} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => choose(lang.code)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted',
                    active && 'font-medium',
                  )}
                >
                  <span>{lang.label}</span>
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
